import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@quake/shared-ui/components/ui/alert";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Label } from "@quake/shared-ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import { Skeleton } from "@quake/shared-ui/components/ui/skeleton";
import { toast } from "@quake/shared-ui/lib/sonner";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import AlertCircleIcon from "lucide-react/dist/esm/icons/circle-alert";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { queryClient, trpc } from "~/utils/trpc";
import { SupportComposer } from "./-components/support-composer";
import { SupportPageHeader, SupportPageShell } from "./-components/support-desk-ui";

export const Route = createFileRoute("/_dashboard/$datasetSlug/support/new")({
  component: NewSupportRequestPage,
});

function NewSupportRequestPage() {
  const { datasetSlug } = Route.useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [categoryId, setCategoryId] = useState("");

  useBreadCrumbs([
    { title: "My support", link: `/${datasetSlug}/support` },
    { title: "New request", link: `/${datasetSlug}/support/new` },
  ]);

  const configQuery = useQuery(trpc.supportDesk.getCustomerConfig.queryOptions());
  const config = configQuery.data;
  const createTicket = useMutation(trpc.supportDesk.create.mutationOptions());

  const submitRequest = async ({
    contentHtml,
    contentText,
    attachmentFileIds,
  }: {
    contentHtml: string;
    contentText: string;
    attachmentFileIds: string[];
  }) => {
    if (!subject.trim() || !categoryId) return;
    const result = await createTicket.mutateAsync({
      subject: subject.trim(),
      categoryId,
      contentHtml,
      contentText,
      attachmentFileIds,
    });
    await queryClient.invalidateQueries({ queryKey: trpc.supportDesk.pathKey() });
    toast.success(`Support request ${result.ticketId} received`);
    await navigate({
      to: "/$datasetSlug/support/$ticketId",
      params: { datasetSlug, ticketId: result.ticketId },
    });
  };

  return (
    <SupportPageShell>
      <SupportPageHeader
        title="New support request"
        description="Tell us what happened. Quake Support will reply here and by email."
        datasetSlug={datasetSlug}
        back
      />

      <div className="mx-auto w-full max-w-3xl py-5">
        {configQuery.isPending ? <NewRequestSkeleton /> : null}
        {configQuery.isError ? (
          <Alert variant="destructive">
            <AlertCircleIcon className="h-4 w-4" />
            <AlertTitle>Categories could not be loaded</AlertTitle>
            <AlertDescription>{configQuery.error.message}</AlertDescription>
          </Alert>
        ) : null}

        {configQuery.isSuccess ? (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_14rem]">
              <div className="space-y-1.5">
                <Label htmlFor="support-subject">Subject</Label>
                <Input
                  id="support-subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Briefly describe the issue"
                  maxLength={200}
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="support-category">Category</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger id="support-category" className="cursor-pointer">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.categories.map((category) => (
                      <SelectItem key={category.id} value={category.id} className="cursor-pointer">
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {createTicket.isError ? (
              <Alert variant="destructive">
                <AlertCircleIcon className="h-4 w-4" />
                <AlertTitle>Request was not submitted</AlertTitle>
                <AlertDescription>{createTicket.error.message}</AlertDescription>
              </Alert>
            ) : null}

            <div className="border-t border-border/70 pt-5">
              <SupportComposer
                label="What can we help with?"
                placeholder="Include what you expected, what happened, and any steps we can use to reproduce it."
                hint="Rich text and up to 10 files are supported."
                submitLabel="Submit request"
                isPending={createTicket.isPending}
                disabled={!subject.trim() || !categoryId}
                onSubmit={submitRequest}
              />
              {(!subject.trim() || !categoryId) && (
                <p className="mt-2 text-right text-xs text-muted-foreground">
                  Add a subject and category before submitting.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </SupportPageShell>
  );
}

function NewRequestSkeleton() {
  return (
    <div className="space-y-5" aria-label="Loading new support request">
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_14rem]">
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
      <Skeleton className="h-52 w-full" />
    </div>
  );
}
